import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { changeLanguage } from '../i18n';
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Logout as LogoutIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { auth } from '../services/api';

function Profile({ onLogout }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    email: '',
    language: 'fr',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await auth.getProfile();
      setProfile(prev => ({
        ...prev,
        first_name: response.data.first_name || '',
        last_name: response.data.last_name || '',
        email: response.data.email || '',
        language: response.data.language || 'fr'
      }));
    } catch (error) {
      console.error(error);
      setSnackbar({ open: true, message: t('profileLoadError'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (profile.new_password && profile.new_password !== profile.confirm_password) {
      setSnackbar({ open: true, message: t('passwordMismatch'), severity: 'error' });
      return;
    }

    if (profile.new_password && !profile.current_password) {
      setSnackbar({ open: true, message: t('currentPasswordRequired'), severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        first_name: profile.first_name,
        last_name: profile.last_name,
        email: profile.email,
        language: profile.language
      };

      if (profile.new_password) {
        payload.current_password = profile.current_password;
        payload.new_password = profile.new_password;
      }

      const response = await auth.updateProfile(payload);

      // Mettre à jour le localStorage avec les nouvelles infos
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Mettre à jour la langue de l'interface si elle a changé
      if (profile.language !== i18n.language) {
        changeLanguage(profile.language);
      }

      // Réinitialiser les champs de mot de passe
      setProfile(prev => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: ''
      }));

      setSnackbar({ open: true, message: t('profileUpdateSuccess'), severity: 'success' });
    } catch (error) {
      const errorMessage = error.response?.data?.error || t('profileUpdateError');
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar sx={{ py: 1 }}>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
            {t('myProfile')}
          </Typography>
          <IconButton color="inherit" onClick={onLogout}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3 }}>
          <form onSubmit={handleSubmit}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              {t('personalInfo')}
            </Typography>

            <TextField
              fullWidth
              label={t('firstName')}
              value={profile.first_name}
              onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
              required
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label={t('lastName')}
              value={profile.last_name}
              onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
              required
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label={t('email')}
              type="email"
              value={profile.email}
              onChange={(e) => setProfile({ ...profile, email: e.target.value })}
              required
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 4 }}>
              <InputLabel>{t('language')}</InputLabel>
              <Select
                value={profile.language}
                onChange={(e) => setProfile({ ...profile, language: e.target.value })}
                label={t('language')}
              >
                <MenuItem value="fr">Français</MenuItem>
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="pt">Português</MenuItem>
                <MenuItem value="mg">Malagasy</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              {t('changePassword')}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('changePasswordHint')}
            </Typography>

            <TextField
              fullWidth
              label={t('currentPassword')}
              type="password"
              value={profile.current_password}
              onChange={(e) => setProfile({ ...profile, current_password: e.target.value })}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label={t('newPassword')}
              type="password"
              value={profile.new_password}
              onChange={(e) => setProfile({ ...profile, new_password: e.target.value })}
              helperText={t('passwordMinLength')}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label={t('confirmPassword')}
              type="password"
              value={profile.confirm_password}
              onChange={(e) => setProfile({ ...profile, confirm_password: e.target.value })}
              sx={{ mb: 4 }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              startIcon={<SaveIcon />}
              disabled={saving}
            >
              {saving ? t('saving') : t('save')}
            </Button>
          </form>
        </Paper>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Profile;
